import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { AuditTrailForExport, getAuditTrailData } from "../util/getAuditTrailData";
import { uploadJsonToS3 } from "../util/AppUtils";

const baseUrl = BASE_URL + "types";

// 📌 Create Type (POST)
export const createType = createAsyncThunk(
  "type/create",
  async (typeData, { rejectWithValue }) => {
    try {
      const version_content = {
        ...typeData,
        orders: typeData.orders.toString(),
        action_to_show: `Created Type ${typeData.name}`,
      };

      const new_content_snapshot = await uploadJsonToS3(
        version_content,
        "type_created",
        "CREATE",
        true
      );

      const ad = await getAuditTrailData({
        action: "CREATE_TYPE",
        comment: `Created Type Name ${typeData.name}`,
        version_content: {
          action_to_show: version_content.action_to_show,
        },
        new_content_snapshot,
      });

      const payload = {
        ...typeData,
        orders:typeData.orders.toString(),
        audit_trail_data: ad,
      };

      if(typeData.is_flowchart || typeData.is_allow_record){
        payload.is_procedure = true
      }

      const response = await axios.post(`${baseUrl}/create`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error creating type");
    }
  }
);

// 📌 Update Type (PUT)
export const updateType = createAsyncThunk(
  "type/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const version_content = {
        ...data,
        orders: data.orders.toString(),
        action_to_show: "Edit Type Pop-Up Table",
      };

      const new_content_snapshot = await uploadJsonToS3(
        version_content,
        "type_updated",
        "UPDATE",
        true
      );

      const ad = await getAuditTrailData({
        action: "UPDATE_TYPE",
        comment: `Update Type Name ${data.name}`,
        version_content:{action_to_show:"Edit Type Pop-Up Table"},
        parent_version:data.version_id_ref,
        new_content_snapshot,
      });
                

      const payload = {
        ...data,
        orders:data.orders.toString(),
        audit_trail_data: ad,
      };

      if(data.is_flowchart || data.is_allow_record){
        payload.is_procedure = true
      }


      const response = await axios.put(`${baseUrl}/update/${id}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating type");
    }
  }
);

// 📌 Get Type by ID (GET)
export const getTypeById = createAsyncThunk(
  "type/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-by-id/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching type");
    }
  }
);

// 📌 Get All Types (GET)
export const getAllTypes = createAsyncThunk(
  "type/getAll",
  async ({ limit = 10000000, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=${limit}&offset=${offset}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching types");
    }
  }
);

export const fetchAllTypes = createAsyncThunk(
  "type/fetchAll",
  async ({ searchQuery}, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?${searchQuery}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching types");
    }
  }
);

// 📌 Delete Type (DELETE)
export const deleteType = createAsyncThunk(
  "type/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${baseUrl}/delete/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting type");
    }
  }
);

export const exportCSV = createAsyncThunk(
  "type/exportCSV",
  async (_, { rejectWithValue }) => {
    try {

      await AuditTrailForExport({
        apiUrl:`${baseUrl}/export-csv`,
        file_name:"export-types-data-"+Date.now()+".csv",
        action:"EXPORT_TYPES_DATA",
        action_to_show:"Types Table data exported as CSV"
      })
  
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error exporting CSV");
    }
  }
);

export const uploadCSV = createAsyncThunk(
  "type/uploadCSV",
  async ({ csvFile, auditTrailData }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", csvFile);
      formData.append("audit_trail_data", JSON.stringify(auditTrailData));

      const response = await axios.post(`${baseUrl}/upload-csv`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error uploading CSV");
    }
  }
);

// 🛠 Type Slice
const typeSlice = createSlice({
  name: "type",
  initialState: {
    types: [],
    pagination:{},
    type: null,
    loading: false,
    error: null,
    uploadError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createType.pending, (state) => {
        state.loading = true;
      })
      .addCase(createType.fulfilled, (state, action) => {
        state.loading = false;
        // state.types.push(action.payload);
      })
      .addCase(createType.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.message || action.payload.error || "An error occured";
      })

      .addCase(exportCSV.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportCSV.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportCSV.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.message || action.payload.error || "An error occured";
      })

      // Upload CSV
      .addCase(uploadCSV.pending, (state) => {
        state.loading = true;
        state.uploadError = null;
      })
      .addCase(uploadCSV.fulfilled, (state) => {
        state.loading = false;
        state.uploadError = null;
      })
      .addCase(uploadCSV.rejected, (state, action) => {
        state.loading = false;
        state.uploadError =
          action.payload.message || action.payload.error || "An error occured";
      })

      .addCase(updateType.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateType.fulfilled, (state, action) => {
        state.loading = false;
        state.types = state.types.map((type) =>
          type.id === action.payload.id ? action.payload : type
        );
      })
      .addCase(updateType.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.message || action.payload.error || "An error occured";
      })
      .addCase(getTypeById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTypeById.fulfilled, (state, action) => {
        state.loading = false;
        state.type = action.payload;
      })
      .addCase(getTypeById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.message || action.payload.error || "An error occured";
      })
      .addCase(getAllTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.types = action.payload.results;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getAllTypes.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.message || action.payload.error || "An error occured";
      })
      .addCase(fetchAllTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.types = action.payload.results;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchAllTypes.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.message || action.payload.error || "An error occured";
      })
      .addCase(deleteType.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteType.fulfilled, (state, action) => {
        state.loading = false;
        state.types = state.types.filter((type) => type.id !== action.payload);
      })
      .addCase(deleteType.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.message || action.payload.error || "An error occured";
      });
  },
});

export default typeSlice.reducer;
